import { memo, useEffect, useState, useRef } from "react";
import { 
  Card, 
  Radio, 
  RadioOption,
  Button
} from "@aidron/aidron-ds";

import PropTypes from 'prop-types';

function TableScrollButtons({
  scrollToSide,
  isScrollingDivAtBeginning,
  isScrollingDivAtEnd,
}) {

  return (
    <div className="d-flex justify-content-between mb-3}">
      <Button
        label=""
        type="outlined"
        icon="arrow-left"
        onClick={() => scrollToSide("left")}
        disabled={!isScrollingDivAtBeginning()}
        className="ms-2"
      />
      <Button
        label=""
        type="outlined"
        icon="arrow-right"
        onClick={() => scrollToSide("right")}
        disabled={isScrollingDivAtEnd()}
        className="me-3"
      />
    </div>
  );
};

function TableFadesComponent({
  freezeColumns,
  isScrollingDivAtBeginning,
  isScrollingDivAtEnd,
}) {
  return (
    <>
      {isScrollingDivAtBeginning() && !freezeColumns ? (
        <div className="table-fade-left"></div>
      ) : (
        <></>
      )}
      {isScrollingDivAtEnd() ? <></> : <div className="table-fade-right"></div>}
    </>
  );
};

const TableFades = memo(TableFadesComponent);

function TableControlsComponent({
  children,
  freezeColumns,
  selectedUnit,
  setSelectedUnit,
  unitOptions,
  initialScrollIndex,
  hideControls,
  accuracyTableScrollBehavior = false,
  plavVsForecastTableScrollBehavior = false,
  adjustPlanTableScrollBehavior = false,
  recomendationTableScrollBehavior = false,
  salesTable = false
}) {

  const scrollingDiv = useRef(null);
  const nonScrollingDiv = useRef(null);
  const maxScrollPosition = scrollingDiv.current?.scrollWidth - nonScrollingDiv.current?.offsetWidth;

  const [scrollingDivPosition, setScrollingDivPosition] = useState(0);
  const [hasScrolled, setHasScrolled] = useState(false);

  useEffect(() => {
    if (hasScrolled) return;

    let shouldScroll = false;
    const delay = 200;

    if (plavVsForecastTableScrollBehavior) {
      shouldScroll = true;
    } else if (accuracyTableScrollBehavior) {
      shouldScroll = true;
    } else if (adjustPlanTableScrollBehavior) {
      shouldScroll = true;
    } else if (initialScrollIndex !== undefined && initialScrollIndex > 0) {
      shouldScroll = true;
    } else if (recomendationTableScrollBehavior) {
      shouldScroll = true;
    }

    if (shouldScroll) {
      const timer = setTimeout(() => {
        executeScrollStrategy();
      }, delay);

      return () => clearTimeout(timer);
    }
  }, [
    hasScrolled, 
    plavVsForecastTableScrollBehavior, 
    accuracyTableScrollBehavior, 
    initialScrollIndex,
    recomendationTableScrollBehavior
  ]);

 const scrollTo = (point) => {
    if (scrollingDiv.current) scrollingDiv.current.scrollTo({ top: 0, left: point, behavior: "smooth" });
  }

  const updateScrollingDivPosition = () => setScrollingDivPosition(scrollingDiv.current.scrollLeft);

  const scrollToSide = (buttonClicked) => {
    const leftOrRight = buttonClicked === "left" ? -300 : 300;
    let desiredMonthPosition = scrollingDiv.current.scrollLeft + leftOrRight;

    scrollTo(desiredMonthPosition);
  }

  const isScrollingDivAtBeginning = () => scrollingDivPosition >= 1;

  const isScrollingDivAtEnd = () => scrollingDivPosition >= maxScrollPosition;

  const scrollToPlavVsForecastColumn = () => {
    const table = scrollingDiv.current.querySelector('table');
    if (!table) return;

    const headerCells = table.querySelectorAll('thead tr:first-child th');
    
    if (headerCells.length > 0) {
      const targetColumn = headerCells[1];
      let scrollPosition = targetColumn.offsetLeft;
      scrollPosition += 200;
      
      scrollingDiv.current.scrollTo({ 
        left: Math.max(0, scrollPosition),
        behavior: 'smooth' 
      });
    }
  };

  const scrollToAccuracyColumn = () => {
    const table = scrollingDiv.current.querySelector('table');
    if (!table) return;

    const headerCells = table.querySelectorAll('thead tr:first-child th');
    
    if (headerCells.length > 0) {
      const targetColumn = headerCells[2] ?? headerCells[1];
      let scrollPosition = targetColumn?.offsetLeft ?? 0;
      
      scrollPosition += 40;
      
      scrollingDiv.current.scrollTo({ 
        left: Math.max(0, scrollPosition),
        behavior: 'smooth' 
      });
    }
  };

  const scrollToAdjustPlanColumn = () => {
    const table = scrollingDiv.current.querySelector('table');
    if (!table) return;

    const headerCells = table.querySelectorAll('thead tr:first-child th');
    
    if (headerCells.length > 0) {
      const targetColumn = headerCells[0];
      let scrollPosition = targetColumn.offsetLeft;
      
      scrollingDiv.current.scrollTo({ 
        left: Math.max(0, scrollPosition),
        behavior: 'smooth' 
      });
    }
  };

  const scrollToRecomendationTableColumn = () => {
    const table = scrollingDiv.current.querySelector('table');
    if (!table) return;

    const headerCells = table.querySelectorAll('thead tr:first-child th');

    const index = 1;

    if (index >= 0 && index < headerCells.length) {
      const targetColumn = headerCells[index];
      let scrollPosition = targetColumn.offsetLeft;

      scrollingDiv.current.scrollTo({
        left: Math.max(0, scrollPosition),
        behavior: 'smooth'
      });
    }
  };

  const executeScrollStrategy = () => {
    if (!scrollingDiv.current) return;

    if (plavVsForecastTableScrollBehavior) {
      scrollToPlavVsForecastColumn();
    } else if (accuracyTableScrollBehavior) {
      scrollToAccuracyColumn();
    } else if(adjustPlanTableScrollBehavior) {
      scrollToAdjustPlanColumn();
    } else if (initialScrollIndex !== undefined) {
      scrollTo(initialScrollIndex);
    } else if(recomendationTableScrollBehavior) {
      scrollToRecomendationTableColumn();
    }

    setHasScrolled(true);
  };  

  return (
    <Card style={{ padding: "1rem", inlineSize: "100%", marginBlockStart: salesTable ? "-3rem" : "" }}>
      {selectedUnit && (
        <div className="d-flex justify-content-end">
          <div className="me-3 mb-2">
            <Radio label="" selected={selectedUnit} handleChange={setSelectedUnit} className="d-flex">
              {unitOptions.map((option) => (
                <RadioOption value={option.value} key={option.displayName}>
                  {option.displayName}
                </RadioOption>
              ))}
            </Radio>
          </div>
        </div>
      )}

      {!hideControls && (
        <TableScrollButtons
          scrollToSide={scrollToSide}
          isScrollingDivAtBeginning={isScrollingDivAtBeginning}
          isScrollingDivAtEnd={isScrollingDivAtEnd}
        />
      )}

      <div className="position-relative" ref={nonScrollingDiv}>
        <TableFades
          style={{ backgroundColor: "red" }}
          freezeColumns={freezeColumns}
          isScrollingDivAtBeginning={isScrollingDivAtBeginning}
          isScrollingDivAtEnd={isScrollingDivAtEnd}/>

        <div
          className="table-inner-div position-relative overflow-scroll"
          ref={scrollingDiv}
          onScroll={updateScrollingDivPosition}>

          {children}
        </div>
      </div>
    </Card>
  );
}

export const TableControls = memo(TableControlsComponent);

TableScrollButtons.propTypes = {
  scrollToSide: PropTypes.func,
  isScrollingDivAtBeginning: PropTypes.func,
  isScrollingDivAtEnd: PropTypes.func
};

TableFadesComponent.propTypes = {
  freezeColumns: PropTypes.bool,
  isScrollingDivAtBeginning: PropTypes.func,
  isScrollingDivAtEnd: PropTypes.func
};

TableControlsComponent.propTypes = {
  children: PropTypes.node.isRequired,
  freezeColumns: PropTypes.bool,
  hideControls: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.oneOf([undefined])
  ]),
  initialScrollIndex: PropTypes.number,
  selectedUnit: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.oneOf([undefined])
  ]),
  setSelectedUnit: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.oneOf([undefined])
  ]),
  unitOptions: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.oneOf([undefined])
  ]),
  plavVsForecastTableScrollBehavior: PropTypes.bool,
  accuracyTableScrollBehavior: PropTypes.bool,
  adjustPlanTableScrollBehavior: PropTypes.bool,
  recomendationTableScrollBehavior: PropTypes.bool,
  salesTable: PropTypes.bool
};